import type { NetworkPoiExportItem } from '@/api/poisApi';
import { getLeakEntityStatusDefinition, getPoiEntityStatusDefinition } from '@/components/map/utils/entityStatus';
import type { LeakEntityStatus, PoiEntityStatus } from '@/components/map/utils/entityStatus';
import { exportRowsToFile, type ExportFormat } from './exportWorkbook';
import { formatIsoDate } from './dateUtils';

/** @deprecated Use {@link ExportFormat}. Kept as an alias for existing importers. */
export type PoiExportFormat = ExportFormat;

const PIPE_TYPE_LABELS: Record<number, string> = {
    0: 'Comms',
    1: 'CSPL',
    2: 'Mains',
    3: 'Fitting',
    4: 'ST/BB',
};

/**
 * One spreadsheet row = one projected entity. A confirmed root record produces a POI row
 * AND a leak row (its derived first leak); an unconfirmed root produces a POI row only;
 * a child record produces a leak row only — the same projection the map renders.
 */
export interface EntityExportRow {
    entityType: 'poi' | 'leak';
    item: NetworkPoiExportItem;
}

export function buildPoiExportHeaders(): string[] {
    return [
        'Type',
        'Area',
        'DMA',
        'DMA Name',
        'Worklist',
        'Stop',
        'Surveyed Date',
        'W3W',
        'Longitude',
        'Latitude',
        'Status',
        'Pipe Type',
        'Search Date',
        'Repaired?',
    ];
}

/** "21" for roots, "21-1" for child leaks — matching the table and map labels. */
function stopLabel(item: NetworkPoiExportItem): string {
    return item.suffix != null ? `${String(item.stop)}-${String(item.suffix)}` : String(item.stop);
}

/**
 * Maps one projected entity to its spreadsheet cells.
 *
 * Coordinates and W3W are per entity, matching where the map renders each marker: a POI
 * row at the record's own point; a derived first leak at the recorded leak location,
 * falling back to the POI point when the leak was "as reported"; a child leak at its own
 * point. Status labels come from the entity registries (the full split vocabulary — every
 * status has a label by construction), with unknown wire values presenting as
 * Unclassified per the shared tolerance rule. "Repaired?" is a leak-entity concept and is
 * left blank on POI rows; the leak status itself already distinguishes Repaired from
 * No Dig® Repaired, which is why the old "No Dig Repair?" column is gone.
 */
export function buildEntityExportRow({ entityType, item }: EntityExportRow): unknown[] {
    const isChild = item.poiEntityStatus == null;
    const isLeakRow = entityType === 'leak';
    const useLeakLocation = isLeakRow && !isChild;

    const status = isLeakRow
        ? getLeakEntityStatusDefinition((item.leakEntityStatus ?? 'unclassified') as LeakEntityStatus).label
        : getPoiEntityStatusDefinition((item.poiEntityStatus ?? 'unclassified') as PoiEntityStatus).label;

    return [
        isLeakRow ? 'Leak' : 'POI',
        item.areaName,
        item.dmaIdent,
        item.dmaName,
        // A leak's worklist is its family's worklist — same group either way.
        item.groupName,
        stopLabel(item),
        formatIsoDate(item.surveyedDate) ?? '',
        useLeakLocation ? (item.leakWhatThreeWords ?? item.whatThreeWords) : item.whatThreeWords,
        useLeakLocation ? (item.leakLongitude ?? item.longitude) : item.longitude,
        useLeakLocation ? (item.leakLatitude ?? item.latitude) : item.latitude,
        status,
        item.pipeType != null ? (PIPE_TYPE_LABELS[item.pipeType] ?? '') : '',
        formatIsoDate(item.searchDate) ?? '',
        isLeakRow ? (item.fixed === true ? 'Yes' : item.fixed === false ? 'No' : '') : '',
    ];
}

/**
 * Generates and triggers a download for the given entity rows in the requested format
 * (Excel .xlsx or .csv).
 *
 * Callers are expected to select the rows beforehand (see selectEntityExportRows).
 * Returns true when a file was downloaded, false when the list was empty (allowing the
 * caller to show an appropriate "no data" message).
 */
export async function exportPois(rows: EntityExportRow[], fileName: string, format: ExportFormat): Promise<boolean> {
    return exportRowsToFile({
        sheetName: 'POIs & Leaks',
        headers: buildPoiExportHeaders(),
        rows: rows.map(buildEntityExportRow),
        fileName,
        format,
    });
}
